import type { ChipId } from '../../data/chips';

// 16×16 pixel icons for chip faces (TERMINAL.md §6.1, style guide §6).
// Original designs; '.' is transparent, other characters map to ICON_PALETTE.

export const ICON_PALETTE: Record<string, string> = {
  k: '#141414',
  w: '#f2f2e8',
  g: '#9aa0a6',
  d: '#4a4f55',
  y: '#ffd166',
  o: '#ff8a3d',
  r: '#e04040',
  b: '#5f86ff',
  c: '#9fe8ff',
  G: '#3fcf6a',
  l: '#b8ffb0',
  p: '#7a4fd0',
};

const BASE_ICONS = {
  cannon: [
    '................',
    '......kkkk......',
    '......kcck......',
    '......kbbk......',
    '......kbbk......',
    '......kbbk......',
    '......kbbk......',
    '.....kkbbkk.....',
    '....kggbbggk....',
    '...kggggggggk...',
    '...kgddggddgk...',
    '...kgddggddgk...',
    '...kggggggggk...',
    '....kkkkkkkk....',
    '....kdk..kdk....',
    '....kkk..kkk....',
  ],
  sword: [
    '................',
    '............kkk.',
    '...........kwwk.',
    '..........kwwk..',
    '.........kwwk...',
    '........kwwk....',
    '.......kwwk.....',
    '......kwwk......',
    '..kk.kwwk.......',
    '..kykkwk........',
    '...kyyk.........',
    '...kkyyk........',
    '..kook.kk.......',
    '.kook...........',
    '.kkk............',
    '................',
  ],
  widesword: [
    '................',
    '..kkkkkkkkkkkk..',
    '.kwwwwwwwwwwwwk.',
    '.kwggggggggggwk.',
    '..kwwwwwwwwwwk..',
    '...kkkkkkkkkk...',
    '.......kk.......',
    '......kyyk......',
    '.....kyyyyk.....',
    '......kyyk......',
    '......kook......',
    '......kook......',
    '......kook......',
    '......kyyk......',
    '.......kk.......',
    '................',
  ],
} satisfies Record<string, readonly string[]>;

const B = BASE_ICONS;

/** Dedicated v0.1 field/control pictograms in the established cartridge palette. */
const PLAYTEST_ICONS = {
  areagrab: [
    '..rrrrrrrrrrrr..',
    '..rkkkkkkkkkkr..',
    '..rrrrrrrrrrrr..',
    '.......yy.......',
    '......yyyy......',
    '.....yyyyyy.....',
    '....yyy..yyy....',
    '.......yy.......',
    '.......yy.......',
    '.......yy.......',
    '..bbbbbbbbbbbb..',
    '..bkkkkkkkkkkb..',
    '..bbbbbbbbbbbb..',
    '................',
    '................',
    '................',
  ],
  mine: [
    '................',
    '.......rr.......',
    '......ryyr......',
    '.....ryyyyr.....',
    '....rrpppprr....',
    '...rkppppppkr...',
    '..rkpppwwpppkr..',
    '..rkppwwwwppkr..',
    '..rkppwwwwppkr..',
    '..rkpppwwpppkr..',
    '...rkppppppkr...',
    '....rrpppprr....',
    '..kkkkkkkkkkkk..',
    '..kddddddddddk..',
    '..kkkkkkkkkkkk..',
    '................',
  ],
  block: [
    '................',
    '......kkkk......',
    '.....kcccck.....',
    '....kcccccck....',
    '...kkkkkkkkkk...',
    '...kbbbbbbbbk...',
    '...kbbwwbbbbk...',
    '...kbbbbbbbbk...',
    '...kbbbbddddk...',
    '...kbbbdddddk...',
    '...kbbddddddk...',
    '...kddddddddk...',
    '...kkkkkkkkkk...',
    '................',
    '................',
    '................',
  ],
  break: [
    'kkkkkkkkkkkkkkkk',
    'kppppppppppppppk',
    'kppppppkkppppppk',
    'kpppppkkkkpppppk',
    'kppppkk..kkppppk',
    'kpppkk....kkpppk',
    'kppkk......kkppk',
    'kpkk........kkpk',
    'kpkk........kkpk',
    'kppkk......kkppk',
    'kpppkk....kkpppk',
    'kppppkk..kkppppk',
    'kpppppkkkkpppppk',
    'kppppppkkppppppk',
    'kppppppppppppppk',
    'kkkkkkkkkkkkkkkk',
  ],
  airshot: [
    '................',
    '............c...',
    '...........cc...',
    '..........ccc...',
    '..kkkkkkkkcccc..',
    '.kbbbbbbbbcccck.',
    '.kbbbbbbbbcccck.',
    '..kkkkkkkkcccc..',
    '..........ccc...',
    '...........cc...',
    '............c...',
    '..ccc...........',
    '.....ccc........',
    '........ccc.....',
    '................',
    '................',
  ],
  spreader: [
    '.......y........',
    '..c....y....c...',
    '...c...y...c....',
    '....c..y..c.....',
    '.....c.y.c......',
    '......cyc.......',
    '.......r........',
    'yyyyyyyrryyyyyyy',
    '.......r........',
    '......cyc.......',
    '.....c.y.c......',
    '....c..y..c.....',
    '...c...y...c....',
    '..c....y....c...',
    '.......y........',
    '................',
  ],
  guard: [
    '................',
    '......kkkk......',
    '....kkcccckk....',
    '...kccyyyycck...',
    '..kccyyyyyycck..',
    '..kcyykkkkyyck..',
    '..kcykkkkkkyck..',
    '..kcykkkkkkyck..',
    '..kcyykkkkyyck..',
    '...kcyyyyyyck...',
    '...kccyyyycck...',
    '....kccyycck....',
    '.....kcccck.....',
    '......kcck......',
    '.......kk.......',
    '................',
  ],
} satisfies Record<string, readonly string[]>;

/** Every playtest chip has a distinct pictogram; weapon-family art is retained where exact. */
export const CHIP_ICONS: Record<ChipId, readonly string[]> = {
  cannon: B.cannon,
  sword: B.sword,
  areagrab: PLAYTEST_ICONS.areagrab,
  mine: PLAYTEST_ICONS.mine,
  block: PLAYTEST_ICONS.block,
  break: PLAYTEST_ICONS.break,
  airshot: PLAYTEST_ICONS.airshot,
  spreader: PLAYTEST_ICONS.spreader,
  widesword: B.widesword,
  guard: PLAYTEST_ICONS.guard,
};
